import axios from "axios";
import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import type {
	OffPropertyCategory,
	VirtualInspectionContactRequest,
	VirtualInspectionLanguageRequest,
	VirtualInspectionObservation,
	VirtualInspectionObservationRequest,
	VirtualInspectionOffPropertyRequest,
	VirtualInspectionPhoto,
	VirtualInspectionState,
	VirtualInspectionSubmitRequest,
} from "@/type/api";

const LANGUAGE_KEY = "rmo.mosquito-check.lang";
const MODE_KEY = "rmo.mosquito-check.mode";

function loadLanguage(): "en" | "es" {
	try {
		return localStorage.getItem(LANGUAGE_KEY) === "es" ? "es" : "en";
	} catch {
		return "en";
	}
}

// The resident's explicit "View all" choice is a device preference, like the
// language above: one global key, never a server round-trip.
function loadMode(): "guided" | "list" {
	try {
		return localStorage.getItem(MODE_KEY) === "list" ? "list" : "guided";
	} catch {
		return "guided";
	}
}

export const useStoreVirtualInspection = defineStore(
	"rmo-virtualinspection",
	() => {
		// State
		const state = ref<VirtualInspectionState | null>(null);
		const loading = ref(false);
		const notFound = ref(false);
		const saveError = ref<string | null>(null); // CopyKey of the message to toast
		const language = ref<"en" | "es">(loadLanguage());
		const mode = ref<"guided" | "list">(loadMode());
		// Session-only object-URL previews for uploaded photos (RMO has no
		// public image route; the uuid is saved server-side).
		const previews = ref<Map<string, string>>(new Map());

		// Serialize saves so per-key writes reach the server in order.
		let saveQueue: Promise<unknown> = Promise.resolve();
		function enqueue<T>(task: () => Promise<T>): Promise<T> {
			const run = saveQueue.then(task, task);
			saveQueue = run.then(
				() => undefined,
				() => undefined,
			);
			return run;
		}

		function persistLanguage() {
			try {
				localStorage.setItem(LANGUAGE_KEY, language.value);
			} catch {
				// private mode / quota — ignore
			}
		}

		function persistMode() {
			try {
				localStorage.setItem(MODE_KEY, mode.value);
			} catch {
				// private mode / quota — ignore
			}
		}

		function applyState(s: VirtualInspectionState) {
			state.value = s;
			language.value = s.language;
			persistLanguage();
		}

		async function fetchState(
			token: string,
		): Promise<VirtualInspectionState | null> {
			loading.value = true;
			notFound.value = false;
			try {
				const s = (await apiClient.JSONGet(
					`/api/rmo/mosquito-check/${token}`,
				)) as VirtualInspectionState;
				applyState(s);
				return s;
			} catch (e) {
				if (e instanceof axios.AxiosError && e.response?.status === 404) {
					notFound.value = true;
					return null;
				}
				saveError.value = "err_load";
				throw e;
			} finally {
				loading.value = false;
			}
		}

		function setLanguage(lang: "en" | "es") {
			language.value = lang;
			persistLanguage();
		}

		function setMode(m: "guided" | "list") {
			mode.value = m;
			persistMode();
		}

		async function saveLanguage(token: string, lang: "en" | "es") {
			setLanguage(lang);
			await enqueue(() => {
				const body: VirtualInspectionLanguageRequest = { language: lang };
				return apiClient
					.JSONPut(`/api/rmo/mosquito-check/${token}/language`, body)
					.then((resp) => applyState(resp as VirtualInspectionState))
					.catch(() => {
						saveError.value = "err_save";
					});
			});
		}

		function patchObservation(req: VirtualInspectionObservationRequest) {
			if (!state.value) {
				return;
			}
			const obs = state.value.observations;
			const i = obs.findIndex((o) => o.habitat_key === req.habitat_key);
			const patch: Partial<VirtualInspectionObservation> = {
				habitat_key: req.habitat_key,
				present: req.present,
			};
			if (req.larvae_present !== undefined) {
				patch.larvae_present = req.larvae_present;
			}
			if (req.already_fixed !== undefined) {
				patch.already_fixed = req.already_fixed;
			}
			if (i >= 0) {
				obs[i] = { ...obs[i], ...patch };
			} else {
				obs.push({
					habitat_key: req.habitat_key,
					present: req.present,
					larvae_present: req.larvae_present ?? null,
					already_fixed: req.already_fixed ?? null,
					photos: [],
				});
			}
		}

		async function saveObservation(
			token: string,
			req: VirtualInspectionObservationRequest,
		) {
			// Optimistic local patch; server response replaces state on success.
			patchObservation(req);
			await enqueue(() => {
				return apiClient
					.JSONPut(`/api/rmo/mosquito-check/${token}/observation`, req)
					.then((resp) => applyState(resp as VirtualInspectionState))
					.catch(() => {
						saveError.value = "err_save";
					});
			});
		}

		function patchRemoveObservation(habitatKey: string) {
			if (!state.value) {
				return;
			}
			state.value.observations = state.value.observations.filter(
				(o) => o.habitat_key !== habitatKey,
			);
		}

		// Unmarks an observation entirely (row removed; counts it in neither
		// Confirmed nor Clear).
		async function deleteObservation(token: string, habitatKey: string) {
			patchRemoveObservation(habitatKey);
			await enqueue(async () => {
				try {
					const resp = await fetch(
						`/api/rmo/mosquito-check/${token}/observation/${habitatKey}`,
						{ method: "DELETE" },
					);
					if (!resp.ok) {
						throw new Error(`delete failed: ${resp.status}`);
					}
					const s = (await resp.json()) as VirtualInspectionState;
					applyState(s);
				} catch {
					saveError.value = "err_save";
				}
			});
		}

		async function saveOffProperty(
			token: string,
			req: VirtualInspectionOffPropertyRequest,
		) {
			if (state.value) {
				const list = state.value.off_property;
				const i = list.findIndex((o) => o.category === req.category);
				if (i >= 0) {
					list[i] = { ...list[i], description: req.description ?? "" };
				} else {
					list.push({
						category: req.category,
						description: req.description ?? "",
						photos: [],
					});
				}
			}
			await enqueue(() => {
				return apiClient
					.JSONPut(`/api/rmo/mosquito-check/${token}/off-property`, req)
					.then((resp) => applyState(resp as VirtualInspectionState))
					.catch(() => {
						saveError.value = "err_save";
					});
			});
		}

		function patchContact(req: VirtualInspectionContactRequest) {
			if (!state.value) {
				return;
			}
			const st = state.value;
			if (req.property_address !== undefined) {
				st.property_address = req.property_address;
			}
			if (req.geom !== undefined) {
				st.geom = req.geom;
			}
			const cur = st.contact ?? { name: "", email: "", phone: "" };
			st.contact = {
				name: req.name ?? cur.name ?? "",
				email: req.email ?? cur.email ?? "",
				phone: req.phone ?? cur.phone ?? "",
			};
		}

		function patchRemoveOffProperty(category: string) {
			if (!state.value) {
				return;
			}
			state.value.off_property = state.value.off_property.filter(
				(o) => o.category !== category,
			);
		}

		// Removes an off-property block entirely (description + photo).
		async function deleteOffProperty(
			token: string,
			category: OffPropertyCategory,
		) {
			patchRemoveOffProperty(category);
			await enqueue(async () => {
				try {
					const resp = await fetch(
						`/api/rmo/mosquito-check/${token}/off-property/${category}`,
						{ method: "DELETE" },
					);
					if (!resp.ok) {
						throw new Error(`delete failed: ${resp.status}`);
					}
					const s = (await resp.json()) as VirtualInspectionState;
					applyState(s);
				} catch {
					saveError.value = "err_save";
				}
			});
		}

		async function saveContact(
			token: string,
			req: VirtualInspectionContactRequest,
		) {
			patchContact(req);
			await enqueue(() => {
				return apiClient
					.JSONPut(`/api/rmo/mosquito-check/${token}/contact`, req)
					.then((resp) => applyState(resp as VirtualInspectionState))
					.catch(() => {
						saveError.value = "err_save";
					});
			});
		}

		async function submit(
			token: string,
			req: VirtualInspectionSubmitRequest,
		): Promise<boolean> {
			return enqueue(async () => {
				try {
					const resp = (await apiClient.JSONPost(
						`/api/rmo/mosquito-check/${token}/submit`,
						req,
					)) as VirtualInspectionState;
					applyState(resp);
					return true;
				} catch {
					saveError.value = "err_save";
					return false;
				}
			});
		}

		// Starts a fresh weekly round for the same resident/property; returns
		// the new token (same contact, address, and language carried over).
		async function renew(token: string): Promise<string | null> {
			try {
				const resp = (await apiClient.JSONPost(
					`/api/rmo/mosquito-check/${token}/renew`,
					{},
				)) as { token: string };
				if (!resp.token) {
					return null;
				}
				// Clear the completed round so the new one loads fresh.
				state.value = null;
				previews.value = new Map();
				return resp.token;
			} catch {
				saveError.value = "err_load";
				return null;
			}
		}

		function patchObservationPhotos(
			habitatKey: string,
			photos: VirtualInspectionPhoto[],
		) {
			if (!state.value) {
				return;
			}
			const obs = state.value.observations;
			const i = obs.findIndex((o) => o.habitat_key === habitatKey);
			if (i >= 0) {
				obs[i] = { ...obs[i], photos };
			} else {
				obs.push({
					habitat_key: habitatKey,
					present: true,
					larvae_present: null,
					already_fixed: null,
					photos,
				});
			}
		}

		function patchOffPropertyPhotos(
			category: string,
			photos: VirtualInspectionPhoto[],
		) {
			if (!state.value) {
				return;
			}
			const list = state.value.off_property;
			const i = list.findIndex((o) => o.category === category);
			if (i >= 0) {
				list[i] = { ...list[i], photos };
			} else {
				list.push({
					category: category as OffPropertyCategory,
					description: "",
					photos,
				});
			}
		}

		function photosForObservation(
			habitatKey: string,
		): VirtualInspectionPhoto[] {
			return (
				state.value?.observations.find((o) => o.habitat_key === habitatKey)
					?.photos ?? []
			);
		}

		function photosForOffProperty(category: string): VirtualInspectionPhoto[] {
			return (
				state.value?.off_property.find((o) => o.category === category)
					?.photos ?? []
			);
		}

		// Local previews exist only for what this session uploaded — RMO has no
		// public image route, so photos from an earlier session can't be shown.
		// The server appends in upload order, so the files just sent are the
		// tail of the source's list.
		function registerPreviews(photos: VirtualInspectionPhoto[], files: File[]) {
			const added = photos.slice(-files.length);
			files.forEach((file, i) => {
				const photo = added[i];
				if (photo) {
					setPreview(photo.id, URL.createObjectURL(file));
				}
			});
		}

		async function uploadPhotos(
			url: string,
			files: File[],
			photosOf: (next: VirtualInspectionState) => VirtualInspectionPhoto[],
		): Promise<boolean> {
			if (files.length === 0) {
				return true;
			}
			let ok = false;
			await enqueue(async () => {
				const form = new FormData();
				for (const file of files) {
					form.append("image", file, file.name);
				}
				try {
					const resp = await fetch(url, {
						method: "POST",
						body: form,
						// Don't set Content-Type; the browser sets it with the boundary.
					});
					if (!resp.ok) {
						throw new Error(`photo upload failed: ${resp.status}`);
					}
					const next = (await resp.json()) as VirtualInspectionState;
					applyState(next);
					registerPreviews(photosOf(next), files);
					ok = true;
				} catch {
					saveError.value = "err_photo";
				}
			});
			return ok;
		}

		function uploadObservationPhoto(
			token: string,
			habitatKey: string,
			files: File[],
		): Promise<boolean> {
			return uploadPhotos(
				`/api/rmo/mosquito-check/${token}/observation/${habitatKey}/photo`,
				files,
				(next) =>
					next.observations.find((o) => o.habitat_key === habitatKey)?.photos ??
					[],
			);
		}

		function uploadOffPropertyPhoto(
			token: string,
			category: OffPropertyCategory,
			files: File[],
		): Promise<boolean> {
			return uploadPhotos(
				`/api/rmo/mosquito-check/${token}/off-property/${category}/photo`,
				files,
				(next) =>
					next.off_property.find((o) => o.category === category)?.photos ?? [],
			);
		}

		async function removePhoto(
			url: string,
			photoId: string,
			photosOf: () => VirtualInspectionPhoto[],
			patch: (photos: VirtualInspectionPhoto[]) => void,
		) {
			// Drop the photo locally first so the tap feels immediate.
			patch(photosOf().filter((p) => p.id !== photoId));
			clearPreview(photoId);
			await enqueue(async () => {
				try {
					const resp = await fetch(url, { method: "DELETE" });
					if (!resp.ok) {
						throw new Error(`photo delete failed: ${resp.status}`);
					}
					applyState((await resp.json()) as VirtualInspectionState);
				} catch {
					saveError.value = "err_save";
				}
			});
		}

		function removeObservationPhoto(
			token: string,
			habitatKey: string,
			photoId: string,
		) {
			return removePhoto(
				`/api/rmo/mosquito-check/${token}/observation/${habitatKey}/photo/${photoId}`,
				photoId,
				() => photosForObservation(habitatKey),
				(photos) => patchObservationPhotos(habitatKey, photos),
			);
		}

		function removeOffPropertyPhoto(
			token: string,
			category: OffPropertyCategory,
			photoId: string,
		) {
			return removePhoto(
				`/api/rmo/mosquito-check/${token}/off-property/${category}/photo/${photoId}`,
				photoId,
				() => photosForOffProperty(category),
				(photos) => patchOffPropertyPhotos(category, photos),
			);
		}

		function setPreview(key: string, url: string) {
			previews.value.set(key, url);
		}

		function clearPreview(key: string) {
			const url = previews.value.get(key);
			if (url) {
				URL.revokeObjectURL(url);
			}
			previews.value.delete(key);
		}

		return {
			// State
			clearPreview,
			deleteObservation,
			deleteOffProperty,
			fetchState,
			language,
			loading,
			mode,
			notFound,
			previews,
			removeObservationPhoto,
			removeOffPropertyPhoto,
			renew,
			saveContact,
			saveError,
			saveLanguage,
			saveObservation,
			saveOffProperty,
			setLanguage,
			setMode,
			state,
			submit,
			uploadObservationPhoto,
			uploadOffPropertyPhoto,
		};
	},
);
